import React, { useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  CheckCircle2,
  CircleSlash,
  Copy,
  Download,
  ExternalLink,
  FileJson,
  RefreshCcw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Square,
  Wand2
} from "lucide-react";
import {
  categories,
  categoryPackByKey,
  categoryPacks,
  collectionProfiles,
  profileQueryCount,
  sizeClasses,
  sortOptions,
  type CollectionProfile
} from "./shared/catalog";
import { productsToCsv, scoredProductsToCsv } from "./shared/export";
import { mergeScoredProducts, scoreCollectedProduct } from "./shared/recommendation";
import type { ProductResult, Region, ScoredProduct, SearchResponse, SortKey } from "./shared/types";
import "./styles.css";

type Mode = "single" | "autonomous";
type AutoPackSelection = "all" | string;

const defaultTags: Record<Region, string> = {
  CA: "eliteselec028-20",
  US: ""
};

function App() {
  const [mode, setMode] = useState<Mode>("single");
  const [region, setRegion] = useState<Region>("US");
  const [query, setQuery] = useState("luggage set");
  const [category, setCategory] = useState("all");
  const [sizeClass, setSizeClass] = useState("any");
  const [sort, setSort] = useState<SortKey>("relevance");
  const [limit, setLimit] = useState(12);
  const [autoPack, setAutoPack] = useState<AutoPackSelection>("travel_luggage");
  const [profile, setProfile] = useState<CollectionProfile>("standard");
  const [autoLimit, setAutoLimit] = useState(12);
  const [affiliateTagByRegion, setAffiliateTagByRegion] = useState<Record<Region, string>>(() => {
    const saved = window.localStorage.getItem("amazon-affiliate-tags");
    try {
      return saved ? { ...defaultTags, ...JSON.parse(saved) } : defaultTags;
    } catch {
      return defaultTags;
    }
  });
  const [singleResults, setSingleResults] = useState<ProductResult[]>([]);
  const [autoResults, setAutoResults] = useState<ScoredProduct[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [searchUrl, setSearchUrl] = useState("");
  const [status, setStatus] = useState("等待搜尋");
  const [isSingleLoading, setIsSingleLoading] = useState(false);
  const [isAutoRunning, setIsAutoRunning] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");
  const [autoProgress, setAutoProgress] = useState({ done: 0, total: 0, current: "" });
  const abortRef = useRef<AbortController | null>(null);

  const affiliateTag = affiliateTagByRegion[region] || "";
  const visibleRows = mode === "single" ? singleResults : autoResults;

  const summary = useMemo(() => {
    const priced = visibleRows.filter((item) => item.price !== null).length;
    const rated = visibleRows.filter((item) => item.rating !== null).length;
    const native = visibleRows.filter((item) => item.source === "amazon-html").length;
    const selected = autoResults.filter((item) => item.recommendationStatus === "auto_selected").length;
    return { priced, rated, native, selected };
  }, [autoResults, visibleRows]);

  const selectedPacks = useMemo(() => {
    if (autoPack === "all") {
      return categoryPacks;
    }
    const pack = categoryPackByKey(autoPack);
    return pack ? [pack] : [categoryPacks[0]];
  }, [autoPack]);

  async function runSearch(event?: React.FormEvent) {
    event?.preventDefault();
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      setStatus("請先輸入商品關鍵字");
      return;
    }

    setIsSingleLoading(true);
    setStatus("正在抓取 Amazon 商品清單");
    setWarnings([]);

    try {
      const data = await searchAmazon({
        region,
        query: normalizedQuery,
        category,
        sizeClass,
        sort,
        limit,
        affiliateTag
      });
      setSingleResults(data.products);
      setWarnings(data.warnings);
      setSearchUrl(data.searchUrl);
      setLastUpdated(new Date(data.requestedAt).toLocaleString());
      setStatus(`完成，取得 ${data.total} 筆商品`);
    } catch (error) {
      setSingleResults([]);
      setSearchUrl("");
      setStatus(error instanceof Error ? error.message : "搜尋失敗");
    } finally {
      setIsSingleLoading(false);
    }
  }

  async function runAutonomousCollection(event?: React.FormEvent) {
    event?.preventDefault();
    const runId = `auto-${new Date().toISOString().replace(/[:.]/g, "-")}`;
    const collectedAt = new Date().toISOString();
    const queryCount = profileQueryCount(profile);
    const queue = selectedPacks.flatMap((pack) =>
      pack.seedQueries.slice(0, queryCount).map((seedQuery) => ({ pack, seedQuery }))
    );
    const controller = new AbortController();
    abortRef.current = controller;
    setMode("autonomous");
    setAutoResults([]);
    setWarnings([]);
    setSearchUrl("");
    setLastUpdated("");
    setAutoProgress({ done: 0, total: queue.length, current: queue[0]?.seedQuery || "" });
    setStatus(`自主收集開始：${queue.length} 個 seed queries`);
    setIsAutoRunning(true);

    let collected: ScoredProduct[] = [];
    const runWarnings: string[] = [];

    try {
      for (const [index, item] of queue.entries()) {
        if (controller.signal.aborted) {
          break;
        }
        setAutoProgress({ done: index, total: queue.length, current: item.seedQuery });
        setStatus(`正在收集 ${item.pack.label}: ${item.seedQuery}`);

        try {
          const data = await searchAmazon(
            {
              region,
              query: item.seedQuery,
              category: item.pack.category,
              sizeClass: item.pack.sizeClass,
              sort: "relevance",
              limit: autoLimit,
              affiliateTag
            },
            controller.signal
          );
          const scored = data.products.map((product) => {
            const next = scoreCollectedProduct(product, item.pack, item.seedQuery, runId, collectedAt);
            return data.warnings.length ? { ...next, warnings: [...next.warnings, ...data.warnings] } : next;
          });
          collected = mergeScoredProducts([...collected, ...scored]);
          setAutoResults(collected);
          runWarnings.push(...data.warnings.map((warning) => `${item.seedQuery}: ${warning}`));
        } catch (error) {
          if (controller.signal.aborted) {
            break;
          }
          runWarnings.push(`${item.seedQuery}: ${error instanceof Error ? error.message : "搜尋失敗"}`);
        }

        setWarnings([...new Set(runWarnings)].slice(-12));
        setAutoProgress({ done: index + 1, total: queue.length, current: queue[index + 1]?.seedQuery || "" });
        if (index < queue.length - 1) {
          await delay(1500, controller.signal);
        }
      }

      const selected = collected.filter((item) => item.recommendationStatus === "auto_selected").length;
      setStatus(controller.signal.aborted ? `已取消，保留 ${collected.length} 筆結果` : `自主收集完成，自動入選 ${selected} 筆`);
      setLastUpdated(new Date().toLocaleString());
      window.localStorage.setItem(
        "amazon-autonomous-last-run",
        JSON.stringify({ runId, collectedAt, total: collected.length, selected })
      );
    } finally {
      setIsAutoRunning(false);
      abortRef.current = null;
    }
  }

  function cancelAutonomousCollection() {
    abortRef.current?.abort();
    setStatus("正在取消自主收集");
  }

  function updateAffiliateTag(value: string) {
    const next = { ...affiliateTagByRegion, [region]: value };
    setAffiliateTagByRegion(next);
    window.localStorage.setItem("amazon-affiliate-tags", JSON.stringify(next));
  }

  function exportCsv() {
    const csv = mode === "single" ? productsToCsv(singleResults) : scoredProductsToCsv(autoResults);
    downloadFile(csv, `amazon-${mode}-${region.toLowerCase()}-${slug(mode === "single" ? query : autoPack)}.csv`, "text/csv;charset=utf-8");
  }

  function exportJson() {
    const json = JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        mode,
        region,
        searchUrl,
        results: mode === "single" ? singleResults : autoResults
      },
      null,
      2
    );
    downloadFile(json, `amazon-${mode}-${region.toLowerCase()}-${slug(mode === "single" ? query : autoPack)}.json`, "application/json;charset=utf-8");
  }

  async function copyAllLinks() {
    await navigator.clipboard.writeText(visibleRows.map((item) => item.affiliateUrl).join("\n"));
    setStatus(`已複製 ${visibleRows.length} 個 affiliate URL`);
  }

  return (
    <main className="app-shell">
      <section className="toolbar" aria-label="Amazon affiliate search controls">
        <div className="brand-block">
          <span className="eyebrow">Affiliate Ops Tool v0.2</span>
          <h1>Amazon 聯盟商品抓取工具</h1>
          <p>單次搜尋或自主收集 US 優先的內容選題商品清單。</p>
        </div>

        <div className="control-panel">
          <fieldset>
            <legend>模式</legend>
            <div className="segmented">
              <button className={mode === "single" ? "active" : ""} type="button" onClick={() => setMode("single")}>
                單次搜尋
              </button>
              <button className={mode === "autonomous" ? "active" : ""} type="button" onClick={() => setMode("autonomous")}>
                自主收集
              </button>
            </div>
          </fieldset>

          <fieldset>
            <legend>區域</legend>
            <div className="segmented">
              {(["US", "CA"] as Region[]).map((item) => (
                <button
                  className={region === item ? "active" : ""}
                  key={item}
                  type="button"
                  onClick={() => setRegion(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </fieldset>

          <label>
            Affiliate tag
            <input value={affiliateTag} onChange={(event) => updateAffiliateTag(event.target.value)} placeholder="選填" />
          </label>

          {mode === "single" ? (
            <SingleSearchControls
              category={category}
              isLoading={isSingleLoading}
              limit={limit}
              query={query}
              runSearch={runSearch}
              setCategory={setCategory}
              setLimit={setLimit}
              setQuery={setQuery}
              setSizeClass={setSizeClass}
              setSort={setSort}
              sizeClass={sizeClass}
              sort={sort}
            />
          ) : (
            <AutonomousControls
              autoLimit={autoLimit}
              autoPack={autoPack}
              autoProgress={autoProgress}
              cancelAutonomousCollection={cancelAutonomousCollection}
              isRunning={isAutoRunning}
              profile={profile}
              runAutonomousCollection={runAutonomousCollection}
              selectedPacks={selectedPacks}
              setAutoLimit={setAutoLimit}
              setAutoPack={setAutoPack}
              setProfile={setProfile}
            />
          )}
        </div>
      </section>

      <section className="results-panel" aria-label="Amazon product results">
        <div className="results-header">
          <div>
            <span className="eyebrow">{mode === "single" ? "Result Board" : "Autonomous Board"}</span>
            <h2>{mode === "single" ? "商品清單" : "自主推薦清單"}</h2>
            <p>{status}</p>
          </div>
          <div className="actions">
            <button disabled={!visibleRows.length} onClick={copyAllLinks} type="button" title="複製全部 affiliate URL">
              <Copy size={17} />
              <span>複製連結</span>
            </button>
            <button disabled={!visibleRows.length} onClick={exportCsv} type="button" title="匯出 CSV">
              <Download size={17} />
              <span>CSV</span>
            </button>
            <button disabled={!visibleRows.length} onClick={exportJson} type="button" title="匯出 JSON">
              <FileJson size={17} />
              <span>JSON</span>
            </button>
          </div>
        </div>

        <div className="metric-strip" aria-label="Search summary">
          <Metric label="總筆數" value={String(visibleRows.length)} />
          <Metric label={mode === "single" ? "含價格" : "自動入選"} value={String(mode === "single" ? summary.priced : summary.selected)} />
          <Metric label="含評分" value={String(summary.rated)} />
          <Metric label="原頁解析" value={String(summary.native)} />
        </div>

        {mode === "autonomous" && (
          <div className="progress-panel" aria-live="polite">
            <div>
              <strong>
                {autoProgress.done}/{autoProgress.total}
              </strong>
              <span>{autoProgress.current || "等待下一個 seed query"}</span>
            </div>
            <progress max={Math.max(autoProgress.total, 1)} value={autoProgress.done} />
          </div>
        )}

        {searchUrl && (
          <a className="search-url" href={searchUrl} target="_blank" rel="noreferrer">
            <SlidersHorizontal size={16} />
            <span>{searchUrl}</span>
          </a>
        )}

        {warnings.length > 0 && (
          <div className="warning-list" role="status">
            {warnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>商品</th>
                <th>ASIN</th>
                <th>價格</th>
                <th>評分</th>
                <th>評論</th>
                <th>{mode === "single" ? "分數" : "推薦分"}</th>
                <th>{mode === "single" ? "來源" : "狀態"}</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td className="empty-state" colSpan={8}>
                    {mode === "single" ? "選擇條件後開始抓取。" : "選擇品類與深度後開始自主收集。"}
                  </td>
                </tr>
              ) : (
                visibleRows.map((item) => <ResultRow key={`${item.asin}-${isScored(item) ? item.seedQuery : item.title}`} item={item} />)
              )}
            </tbody>
          </table>
        </div>

        {lastUpdated && <p className="timestamp">最後更新：{lastUpdated}</p>}
      </section>
    </main>
  );
}

function SingleSearchControls(props: {
  category: string;
  isLoading: boolean;
  limit: number;
  query: string;
  runSearch: (event?: React.FormEvent) => void;
  setCategory: (value: string) => void;
  setLimit: (value: number) => void;
  setQuery: (value: string) => void;
  setSizeClass: (value: string) => void;
  setSort: (value: SortKey) => void;
  sizeClass: string;
  sort: SortKey;
}) {
  return (
    <form className="mode-panel" onSubmit={props.runSearch}>
      <label>
        商品關鍵字
        <input value={props.query} onChange={(event) => props.setQuery(event.target.value)} placeholder="例如 luggage set" />
      </label>

      <div className="form-grid">
        <label>
          大分類
          <select value={props.category} onChange={(event) => props.setCategory(event.target.value)}>
            {categories.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          尺寸/規格
          <select value={props.sizeClass} onChange={(event) => props.setSizeClass(event.target.value)}>
            {sizeClasses.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="form-grid">
        <label>
          排序
          <select value={props.sort} onChange={(event) => props.setSort(event.target.value as SortKey)}>
            {sortOptions.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          數量
          <input min={1} max={50} type="number" value={props.limit} onChange={(event) => props.setLimit(Number(event.target.value))} />
        </label>
      </div>

      <button className="primary-action" disabled={props.isLoading} type="submit">
        {props.isLoading ? <RefreshCcw className="spin" size={18} /> : <Search size={18} />}
        {props.isLoading ? "抓取中" : "開始抓取"}
      </button>
    </form>
  );
}

function AutonomousControls(props: {
  autoLimit: number;
  autoPack: AutoPackSelection;
  autoProgress: { done: number; total: number; current: string };
  cancelAutonomousCollection: () => void;
  isRunning: boolean;
  profile: CollectionProfile;
  runAutonomousCollection: (event?: React.FormEvent) => void;
  selectedPacks: typeof categoryPacks;
  setAutoLimit: (value: number) => void;
  setAutoPack: (value: AutoPackSelection) => void;
  setProfile: (value: CollectionProfile) => void;
}) {
  const estimatedQueries = props.selectedPacks.length * profileQueryCount(props.profile);
  return (
    <form className="mode-panel" onSubmit={props.runAutonomousCollection}>
      <label>
        推薦品類群
        <select value={props.autoPack} onChange={(event) => props.setAutoPack(event.target.value)}>
          <option value="all">全部優先品類</option>
          {categoryPacks.map((pack) => (
            <option key={pack.key} value={pack.key}>
              {pack.label}
            </option>
          ))}
        </select>
      </label>

      <div className="form-grid">
        <label>
          收集深度
          <select value={props.profile} onChange={(event) => props.setProfile(event.target.value as CollectionProfile)}>
            {collectionProfiles.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          每個 query 數量
          <input min={3} max={30} type="number" value={props.autoLimit} onChange={(event) => props.setAutoLimit(Number(event.target.value))} />
        </label>
      </div>

      <div className="pack-note">
        <Sparkles size={17} />
        <span>
          預計 {estimatedQueries} 個 seed queries；高分商品會自動入選，候選與排除仍保留在匯出檔。
        </span>
      </div>

      <div className="pack-stack">
        {props.selectedPacks.map((pack) => (
          <div className="pack-card" key={pack.key}>
            <strong>{pack.label}</strong>
            <span>{pack.commissionNote}</span>
            <p>{pack.recommendationReason}</p>
          </div>
        ))}
      </div>

      <div className="button-row">
        <button className="primary-action" disabled={props.isRunning} type="submit">
          {props.isRunning ? <RefreshCcw className="spin" size={18} /> : <Wand2 size={18} />}
          {props.isRunning ? "收集中" : "開始自主收集"}
        </button>
        <button className="secondary-action" disabled={!props.isRunning} onClick={props.cancelAutonomousCollection} type="button">
          <Square size={16} />
          取消
        </button>
      </div>
    </form>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ResultRow({ item }: { item: ProductResult | ScoredProduct }) {
  async function copyLink() {
    await navigator.clipboard.writeText(item.affiliateUrl);
  }

  const scored = isScored(item);
  return (
    <tr>
      <td className="product-cell">
        {item.imageUrl ? <img src={item.imageUrl} alt="" loading="lazy" /> : <div className="image-placeholder" />}
        <div>
          <strong>{item.title}</strong>
          <small>{scored ? `${item.categoryPack} / ${item.seedQuery}` : item.notes}</small>
        </div>
      </td>
      <td>{item.asin}</td>
      <td>{item.price === null ? "—" : `${item.currency} ${item.price.toFixed(2)}`}</td>
      <td>{item.rating === null ? "—" : item.rating.toFixed(1)}</td>
      <td>{item.reviewCount === null ? "—" : item.reviewCount.toLocaleString()}</td>
      <td>{scored ? item.productRecommendScore : item.confidenceScore}</td>
      <td>
        {scored ? (
          <span className={`status-pill ${item.recommendationStatus}`}>
            {item.recommendationStatus === "auto_selected" ? <CheckCircle2 size={14} /> : <CircleSlash size={14} />}
            {item.recommendationStatus}
          </span>
        ) : (
          item.source
        )}
      </td>
      <td>
        <div className="row-actions">
          <button onClick={copyLink} type="button" title="複製 affiliate URL">
            <Copy size={16} />
          </button>
          <a href={item.affiliateUrl || item.detailUrl} target="_blank" rel="noreferrer" title="開啟 affiliate URL">
            <ExternalLink size={16} />
          </a>
        </div>
      </td>
    </tr>
  );
}

async function searchAmazon(
  body: {
    region: Region;
    query: string;
    category: string;
    sizeClass: string;
    sort: SortKey;
    limit: number;
    affiliateTag: string;
  },
  signal?: AbortSignal
): Promise<SearchResponse> {
  const response = await fetch("/api/search", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal
  });
  const data = (await response.json()) as SearchResponse | { ok: false; error: string };
  if (!response.ok || !data.ok) {
    throw new Error("error" in data ? data.error : "搜尋失敗");
  }
  return data;
}

function isScored(item: ProductResult | ScoredProduct): item is ScoredProduct {
  return "productRecommendScore" in item;
}

function downloadFile(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function slug(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "products"
  );
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
